import './css/work-portal.css'
import { lazy, StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { domAnimation, LazyMotion, MotionConfig } from 'motion/react'
import { ContentLoading } from '@/components/ContentLoading'
import { AppContainer } from '@/features/shell/AppContainer'
import { createShellLoader } from '@/features/shell/shell-route-loaders'
import { authenticated } from '@/features/iam/auth/authenticated-route'
import { AuthenticationScreen } from '@/features/iam/auth/Authentication'
import {
  createPublicRoutes as createIamPublicRoutes,
  createRoutes as createIamRoutes,
} from '@/features/iam/routes'
import { createRoutes as createEmailConfirmationRoutes } from '@/features/email-confirmation/routes'
import { createRoutes as createLegalRoutes } from '@/features/legal/routes'
import { createRoutes as createBillingRoutes } from '@/features/billing/routes'
import { createRoutes as createCallCenterRoutes } from '@/features/call-center/routes'
import { createRoutes as createOpportunitiesRoutes } from '@/features/opportunities/routes'
import {
  createMobileRoutes as createMobileJobRoutes,
  createRoutes as createJobRoutes,
} from '@/features/jobs/routes'
import { createJobPaneLoader } from '@/features/jobs/job-route-loaders'
import { createRoutes as createNotificationsRoutes } from '@/features/notifications/routes'
import { createRoutes as createDocumentsRoutes } from '@/features/documents/routes'
import {
  createAdminRoutes as createPayoutsAdminRoutes,
  createRoutes as createPayoutsRoutes,
} from '@/features/payouts/routes'
import { createAdminRoutes as createInventoryAdminRoutes } from '@/features/inventory/routes'
import { createRoutes as createAdminRoutes } from '@/features/admin/routes'
import { createRoutes as createUserManagementRoutes } from '@/features/user-management/routes'
import { createRoutes as createClientUserManagementRoutes } from '@/features/client-user-management/routes'
import { createRoutes as createLegendsRoutes } from '@/features/legends/routes'
import { RootStore } from '@/RootStore'
import { keyboardStream } from '@/utils/keyboard-util'
import { pageActivityStream } from '@/infra/page-activity'
import { userActive$ } from '@/utils/browser-util'
import { RootStoreContext } from '@/contexts/root-store-context'
import { ModalStackContextProvider } from '@/components/ModalSheet/ModalStackContext'
import { DocumentTitleProvider } from '@/components/DocumentTitleProvider'
import { isRefreshDesign, refreshClassName } from '@/infra/design'

// The error pages contain illustrations, which don't belong in the main bundle.
const RouterErrorBoundary = lazy(() =>
  import('@/features/shell/error-pages/ErrorPageContainer').then((r) => ({
    default: r.RouterErrorBoundary,
  })),
)

const TechnicalBreakPage = lazy(() =>
  import('@/features/shell/error-pages/ErrorPageContainer').then((r) => ({
    default: r.TechnicalBreakPageContainer,
  })),
)

const ShellContainer = lazy(() => import('@/features/shell/ShellContainer'))

// Matches the authentication screen's own loading state, which usually follows.
const bootLoading = (
  <AuthenticationScreen display="authentication">
    <ContentLoading />
  </AuthenticationScreen>
)

;(function () {
  window.__APP_BOOTED = true
  document.documentElement.classList.toggle(refreshClassName, isRefreshDesign)
  const rootNode = document.getElementById('work-portal')!

  // Created before the router so route loaders can use the features. The
  // router is only read once the app is running.
  const rootStore = new RootStore({
    navigate: (to, options) => void router.navigate(to, options),
    getLocation: () => router.state.location,
    getTime: () => new Date(),
    keyboard$: keyboardStream(window.document),
    pageActivity$: pageActivityStream(true),
    userActive$: userActive$,
  })

  // Short-circuit and render the error page if the backends are down.
  if (window.__TECHNICAL_BREAK__) {
    createRoot(rootNode).render(
      <StrictMode>
        <RootStoreContext value={rootStore}>
          <TechnicalBreakPage />
        </RootStoreContext>
      </StrictMode>,
    )
    return
  }

  const router = createBrowserRouter([
    {
      Component: AppContainer,
      ErrorBoundary: RouterErrorBoundary,
      hydrateFallbackElement: bootLoading,
      children: [
        ...createEmailConfirmationRoutes(),
        ...createLegalRoutes(),
        ...createIamPublicRoutes(rootStore.iam),
        authenticated(rootStore.iam, [
          ...createMobileJobRoutes({
            jobs: rootStore.jobs,
            waitForAuthResolution: rootStore.iam.waitForAuthResolution,
          }),
          {
            path: '/',
            Component: ShellContainer,
            loader: createShellLoader({
              shell: rootStore.shell,
              waitForAuthResolution: rootStore.iam.waitForAuthResolution,
            }),
            // Child navigations keep this route's params, so the call agent
            // gate would otherwise only run on the first load.
            shouldRevalidate: ({
              currentUrl,
              nextUrl,
              defaultShouldRevalidate,
            }) =>
              defaultShouldRevalidate ||
              currentUrl.pathname !== nextUrl.pathname,
            children: [
              ...createIamRoutes(rootStore.iam),
              ...createBillingRoutes(),
              ...createCallCenterRoutes({
                callCenter: rootStore.callCenter,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createOpportunitiesRoutes({
                opportunities: rootStore.opportunities,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createJobRoutes({
                jobs: rootStore.jobs,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createNotificationsRoutes({
                notifications: rootStore.notifications,
                jobPaneLoader: createJobPaneLoader({
                  jobs: rootStore.jobs,
                  waitForAuthResolution: rootStore.iam.waitForAuthResolution,
                }),
              }),
              ...createDocumentsRoutes(),
              ...createPayoutsRoutes({
                payouts: rootStore.payouts,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createAdminRoutes({
                admin: rootStore.admin,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
                serviceManagerRoutes: createInventoryAdminRoutes(),
                payoutsRoutes: createPayoutsAdminRoutes(),
              }),
              ...createUserManagementRoutes({
                userManagement: rootStore.userManagement,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createClientUserManagementRoutes({
                clientUserManagement: rootStore.clientUserManagement,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              ...createLegendsRoutes({
                legends: rootStore.legends,
                waitForAuthResolution: rootStore.iam.waitForAuthResolution,
              }),
              // Unknown paths keep the shell, with an empty content area.
              { path: '*', Component: null },
            ],
          },
        ]),
      ],
    },
  ])

  createRoot(rootNode).render(
    <StrictMode>
      <LazyMotion strict features={domAnimation}>
        <MotionConfig reducedMotion="user">
          <DocumentTitleProvider>
            <RootStoreContext value={rootStore}>
              <ModalStackContextProvider>
                <Suspense fallback={bootLoading}>
                  <RouterProvider router={router} />
                </Suspense>
              </ModalStackContextProvider>
            </RootStoreContext>
          </DocumentTitleProvider>
        </MotionConfig>
      </LazyMotion>
    </StrictMode>,
  )
})()
